import React, { useCallback, useEffect, useState } from 'react';
import { Workflow, Play, Check, X, ChevronDown, Loader2, Ban, Clock, AlertTriangle, MessageSquarePlus, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { workflowAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import WorkflowBuilderScreen from './WorkflowBuilderScreen';
import WorkflowDelegations from './WorkflowDelegations';
import WorkflowInbox from './WorkflowInbox';

const STATUS_STYLES = {
  in_progress: 'bg-blue-500/15 text-blue-600',
  completed: 'bg-green-500/15 text-green-600',
  rejected: 'bg-red-500/15 text-red-600',
  cancelled: 'bg-muted text-muted-foreground',
};

const STEP_STATE_STYLES = {
  pending: 'text-muted-foreground',
  approved: 'text-green-600',
  completed: 'text-green-600',
  rejected: 'text-red-600',
  skipped: 'text-muted-foreground line-through',
};

const SUB_TABS = [
  { id: 'runtime', label: 'My Workflows' },
  { id: 'builder', label: 'Builder' },
  { id: 'delegations', label: 'Delegations' },
  { id: 'inbox', label: 'Notifications' },
];

// Describe a step's approver requirement for display.
const approverLabel = (step) => {
  const a = step.approvers;
  if (a && (a.roles?.length || a.user_ids?.length || a.min_role)) {
    const parts = [];
    if (a.roles?.length) parts.push(a.roles.join('/'));
    if (a.user_ids?.length) parts.push(a.user_ids.map((u) => `user#${u}`).join(', '));
    if (a.min_role) parts.push(`${a.min_role}+`);
    return parts.join(a.mode === 'all_of' ? ' & ' : ' / ');
  }
  return step.assignee_role || 'anyone';
};

const slaBadge = (step) => {
  if (!step.sla_due_at) return null;
  const due = new Date(step.sla_due_at);
  const overdue = due.getTime() < Date.now();
  return (
    <Badge className={overdue ? 'bg-red-500/15 text-red-600' : 'bg-amber-500/15 text-amber-600'}>
      {overdue ? <AlertTriangle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
      {overdue ? 'Overdue' : `Due ${due.toLocaleDateString()}`}
    </Badge>
  );
};

const RuntimeTab = () => {
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templateKey, setTemplateKey] = useState('');
  const [title, setTitle] = useState('');
  const [contextRaw, setContextRaw] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [commentDraft, setCommentDraft] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [tplRes, instRes] = await Promise.all([
        workflowAPI.listTemplates(),
        workflowAPI.listInstances(),
      ]);
      const tpls = tplRes?.data?.templates || [];
      setTemplates(tpls);
      setInstances(instRes?.data?.instances || []);
      setTemplateKey((prev) => prev || tpls[0]?.key || '');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load workflows'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    if (!templateKey) return;
    let context;
    if (contextRaw.trim()) {
      try { context = JSON.parse(contextRaw); }
      catch { return toast.error('Context must be valid JSON (e.g. {"amount": 5000, "department": "finance"})'); }
    }
    try {
      setBusyId('start');
      await workflowAPI.start({ template_key: templateKey, title: title.trim() || undefined, context });
      toast.success('Workflow started');
      setTitle(''); setContextRaw('');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not start workflow'));
    } finally {
      setBusyId(null);
    }
  };

  const act = async (id, action, stepKey) => {
    try {
      setBusyId(`${id}:${stepKey || ''}`);
      await workflowAPI.act(id, { action, step_key: stepKey });
      toast.success(`Step ${action === 'reject' ? 'rejected' : action === 'complete' ? 'completed' : 'approved'}`);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Action failed')); // 403 = not an authorized approver
    } finally {
      setBusyId(null);
    }
  };

  const comment = async (id, stepKey) => {
    const note = (commentDraft[id] || '').trim();
    if (!note) return;
    try {
      setBusyId(`${id}:comment`);
      await workflowAPI.comment(id, { note, step_key: stepKey });
      setCommentDraft((d) => ({ ...d, [id]: '' }));
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not add comment'));
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id) => {
    try {
      setBusyId(`${id}:cancel`);
      await workflowAPI.cancel(id, {});
      toast.success('Workflow cancelled');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not cancel'));
    } finally {
      setBusyId(null);
    }
  };

  const runSla = async (id) => {
    try {
      setBusyId(`${id}:sla`);
      const res = await workflowAPI.runSla(id);
      const s = res?.data?.summary || {};
      toast.success(`SLA run: ${s.reminders || 0} reminders, ${s.overdue || 0} overdue, ${s.escalations || 0} escalations`);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not run SLA'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg"><Workflow className="h-5 w-5 text-primary" /></div>
            Start a workflow
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
            <div className="space-y-1">
              <Label htmlFor="wf-template">Template</Label>
              <select
                id="wf-template"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={templateKey}
                onChange={(e) => setTemplateKey(e.target.value)}
              >
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name} ({t.step_count} steps){t.has_parallel ? ' · parallel' : ''}{t.has_sla ? ' · SLA' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="wf-title">Title (optional)</Label>
              <Input id="wf-title" value={title} placeholder="e.g. Laptop purchase — Design team"
                     onChange={(e) => setTitle(e.target.value)} />
            </div>
            <Button onClick={start} disabled={!templateKey || busyId === 'start'}>
              {busyId === 'start' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-2" /> Start</>}
            </Button>
          </div>
          <div className="mt-3 space-y-1">
            <Label htmlFor="wf-context">Context (optional JSON — drives conditional routing)</Label>
            <Input id="wf-context" value={contextRaw} placeholder='{"amount": 5000, "department": "finance", "location": "NY"}'
                   onChange={(e) => setContextRaw(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground">Workflows</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : instances.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">No workflows yet. Start one above.</p>
          ) : (
            <div className="space-y-3">
              {instances.map((inst) => {
                const activeSteps = inst.active_steps || (inst.current_step ? [inst.current_step] : []);
                const active = inst.status === 'in_progress';
                const open = expandedId === inst.id;
                return (
                  <div key={inst.id} className="rounded-lg border border-border">
                    <div className="flex items-center justify-between gap-3 p-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-foreground text-sm truncate">{inst.title}</p>
                          <Badge className={STATUS_STYLES[inst.status] || ''}>{inst.status.replace('_', ' ')}</Badge>
                          {active && activeSteps.length > 1 && <Badge className="bg-indigo-500/15 text-indigo-600">{activeSteps.length} parallel</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {activeSteps.length ? <>Awaiting: <span className="font-medium">{activeSteps.map((s) => s.name).join(', ')}</span></> : 'No active step'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {active && (
                          <Button size="sm" variant="ghost" disabled={busyId === `${inst.id}:sla`} onClick={() => runSla(inst.id)} title="Run SLA / escalation now">
                            {busyId === `${inst.id}:sla` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                          </Button>
                        )}
                        {active && (
                          <Button size="sm" variant="ghost" disabled={busyId === `${inst.id}:cancel`} onClick={() => cancel(inst.id)} title="Cancel">
                            <Ban className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => setExpandedId(open ? null : inst.id)}>
                          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </Button>
                      </div>
                    </div>

                    {/* Per-active-step action rows (supports parallel groups). */}
                    {active && activeSteps.map((step) => {
                      const isApproval = step.type === 'approval';
                      const bkey = `${inst.id}:${step.key}`;
                      return (
                        <div key={step.key} className="flex items-center justify-between gap-3 px-3 pb-2">
                          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-foreground">{step.name}</span>
                            <span>· approver: {approverLabel(step)}</span>
                            {slaBadge(step)}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button size="sm" disabled={busyId === bkey} onClick={() => act(inst.id, isApproval ? 'approve' : 'complete', step.key)}>
                              {busyId === bkey ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> {isApproval ? 'Approve' : 'Complete'}</>}
                            </Button>
                            <Button size="sm" variant="outline" disabled={busyId === bkey} onClick={() => act(inst.id, 'reject', step.key)}>
                              <X className="h-4 w-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      );
                    })}

                    {open && (
                      <div className="border-t border-border/50 p-3 space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Steps</p>
                            <ol className="space-y-1">
                              {(inst.steps || []).map((s, i) => (
                                <li key={s.key} className="text-xs flex items-center gap-2 flex-wrap">
                                  <span className={`font-medium ${STEP_STATE_STYLES[s.state] || ''}`}>
                                    {i + 1}. {s.name}
                                  </span>
                                  <span className="text-muted-foreground">({s.state}{s.order != null ? ` · grp ${s.order}` : ''})</span>
                                  {s.sla_due_at && slaBadge(s)}
                                </li>
                              ))}
                            </ol>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Audit trail</p>
                            {(inst.history || []).length === 0 ? (
                              <p className="text-xs text-muted-foreground">No actions yet.</p>
                            ) : (
                              <ul className="space-y-1">
                                {inst.history.map((h, i) => (
                                  <li key={i} className="text-xs text-muted-foreground">
                                    <span className="font-medium text-foreground">{h.action}</span> — {h.actor}
                                    {h.proxy_for ? ` (proxy for ${h.proxy_for})` : ''}{h.note ? ` · ${h.note}` : ''}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        {active && (
                          <div className="flex items-center gap-2">
                            <Input
                              value={commentDraft[inst.id] || ''}
                              placeholder="Add a comment…"
                              onChange={(e) => setCommentDraft((d) => ({ ...d, [inst.id]: e.target.value }))}
                            />
                            <Button size="sm" variant="outline" disabled={busyId === `${inst.id}:comment`}
                                    onClick={() => comment(inst.id, activeSteps[0]?.key)}>
                              <MessageSquarePlus className="h-4 w-4 mr-1" /> Comment
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const WorkflowsScreen = () => {
  const [tab, setTab] = useState('runtime');
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap gap-1 border-b border-border">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'runtime' && <RuntimeTab />}
      {tab === 'builder' && <WorkflowBuilderScreen />}
      {tab === 'delegations' && <WorkflowDelegations />}
      {tab === 'inbox' && <WorkflowInbox />}
    </div>
  );
};

export default WorkflowsScreen;
